/**
 * Excel Utility Functions
 * Handles Excel file reading and writing operations
 */

import * as XLSX from 'xlsx';
import {VehicleRow} from '../types';

// Type for Excel row data (array of unknown values)
type ExcelRow = unknown[];

/**
 * Read vehicles from uploaded Excel file
 */
export const readVehiclesFromExcel = (file: File): Promise<VehicleRow[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as ExcelRow[];

        if (jsonData.length === 0) {
          reject(new Error('Το αρχείο Excel είναι κενό. Παρακαλώ ανεβάστε ένα αρχείο με δεδομένα οχημάτων.'));
          return;
        }

        // Check if this is a processed file (has more than 2 columns)
        const firstDataRow = jsonData[1];
        if (firstDataRow && Array.isArray(firstDataRow) && firstDataRow.length > 2) {
          reject(new Error('Αυτό το αρχείο φαίνεται να έχει ήδη επεξεργαστεί. Παρακαλώ ανεβάστε ένα νέο αρχείο με μόνο ΑΦΜ και Αριθμό Κυκλοφορίας, ή κατεβάστε ένα κενό πρότυπο από την κορυφή.'));
          return;
        }

        // Skip header row and convert to VehicleRow objects
        const vehicleData: VehicleRow[] = jsonData
          .slice(1)
          .filter((row): row is ExcelRow => Array.isArray(row) && row[0] && row[1]) // Filter out empty rows
          .map((row) => ({
            vatNumber: String(row[0] || ''),
            licensePlate: String(row[1] || ''),
          }));

        if (vehicleData.length === 0) {
          reject(new Error('Δεν βρέθηκαν έγκυρα δεδομένα οχημάτων. Βεβαιωθείτε ότι το αρχείο περιέχει ΑΦΜ και Αριθμό Κυκλοφορίας.'));
          return;
        }

        resolve(vehicleData);
      } catch (error) {
        reject(new Error('Σφάλμα κατά την ανάγνωση του αρχείου Excel. Βεβαιωθείτε ότι είναι έγκυρο αρχείο .xlsx.'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Σφάλμα κατά την ανάγνωση του αρχείου'));
    };

    reader.readAsBinaryString(file);
  });
};

/**
 * Parse processed Excel results from blob
 */
export const parseExcelResults = (blob: Blob): Promise<VehicleRow[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as ExcelRow[];

        // Parse the results
        const updatedVehicles: VehicleRow[] = jsonData
          .slice(1)
          .filter((row): row is ExcelRow => Array.isArray(row))
          .map((row) => ({
          vatNumber: String(row[0] || ''),
          licensePlate: String(row[1] || ''),
          status: String(row[2] || ''),
          searchDate: String(row[3] || ''),
          vehicleType: String(row[4] || ''),
          insuranceCompany: String(row[5] || ''),
          coverageStart: String(row[6] || ''),
          coverageEnd: String(row[7] || ''),
          message: String(row[8] || ''),
        }));

        resolve(updatedVehicles);
      } catch (error) {
        reject(new Error('Σφάλμα κατά την ανάλυση των αποτελεσμάτων'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Σφάλμα κατά την ανάγνωση των αποτελεσμάτων'));
    };

    reader.readAsBinaryString(blob);
  });
};

/**
 * Convert vehicles array to Excel workbook
 */
export const createExcelWorkbook = (vehicles: VehicleRow[]): XLSX.WorkBook => {
  const ws = XLSX.utils.json_to_sheet(
    vehicles.map((v) => ({
      ΑΦΜ: v.vatNumber,
      'Αριθμός Κυκλοφορίας': v.licensePlate,
    }))
  );

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Vehicles');
  return wb;
};

/**
 * Create Excel blob from vehicles
 */
export const createExcelBlob = (vehicles: VehicleRow[]): Blob => {
  const wb = createExcelWorkbook(vehicles);
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Blob([wbout], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

/**
 * Download Excel file with results
 */
export const downloadExcelResults = (vehicles: VehicleRow[]): void => {
  // Create data array with proper ordering matching backend format
  const data = vehicles.map((v) => {
    return {
      'VAT Number': v.vatNumber || '',
      'License Plate': v.licensePlate || '',
      'Status': v.status || '',
      'Search Date': v.searchDate || '',
      'Vehicle Type': v.vehicleType || '',
      'Insurance Company': v.insuranceCompany || '',
      'Coverage Start': v.coverageStart || '',
      'Coverage End': v.coverageEnd || '',
      'Message/Details': v.message || '',
    };
  });

  const ws = XLSX.utils.json_to_sheet(data);

  // Set column widths for better readability
  const columnWidths = [
    { wch: 15 },  // VAT Number
    { wch: 20 },  // License Plate
    { wch: 18 },  // Status
    { wch: 20 },  // Search Date
    { wch: 25 },  // Vehicle Type
    { wch: 35 },  // Insurance Company
    { wch: 20 },  // Coverage Start
    { wch: 20 },  // Coverage End
    { wch: 50 },  // Message/Details
  ];
  ws['!cols'] = columnWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Results');
  
  // Generate filename with timestamp
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
  XLSX.writeFile(wb, `insurance_results_${timestamp}.xlsx`);
};

/**
 * Validate Excel file
 */
export const validateExcelFile = (file: File): boolean => {
  return file.name.endsWith('.xlsx');
};
